import { useQuery } from "@connectrpc/connect-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";

import { formatNepalTime, nepalToday } from "@yatayat/core/date";
import { StaffRole } from "@yatayat/gen/yatayat/auth/v1/auth_pb";
import { OperatorService, type Trip } from "@yatayat/gen/yatayat/operator/v1/operator_pb";

import { BsDateHint } from "~/components/BsDateHint";
import { FleetAlerts } from "~/components/FleetAlerts";
import { PermitWarning, SeatCounts, TripStatusBadge, TripVehicle, useStaffSessionExpiry } from "~/components/OperatorTrip";
import { Empty, Loading, QueryError } from "~/components/QueryState";
import { useI18n } from "~/lib/i18n";
import { formatIsoDate } from "~/lib/labels";
import { retryTransient } from "~/lib/rpc/errors";
import { searchString } from "~/lib/search";
import { can, useStaffSession } from "~/lib/staff";
import { ui } from "~/lib/ui";

type TripsSearch = { date?: string };

// The date lives in the URL so going back from a trip restores it.
export const Route = createFileRoute("/operator/")({
  validateSearch: (search: Record<string, unknown>): TripsSearch => ({
    date: searchString(search.date),
  }),
  component: OperatorTrips,
});

const isoDate = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;

function OperatorTrips() {
  const { t, lang } = useI18n();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const today = nepalToday();
  const date = search.date && isoDate.test(search.date) ? search.date : today;
  // Counter sales, web bookings and boardings change the counts all day.
  const trips = useQuery(OperatorService.method.listTrips, { date }, { retry: retryTransient, refetchInterval: 15_000 });

  const unauthenticated = useStaffSessionExpiry(trips.error);

  const role = useStaffSession().session?.role ?? StaffRole.UNSPECIFIED;

  const heading = date === today ? t("todaysTrips") : t("tripsOnDate", { date: formatIsoDate(date, lang) });

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className={ui.h1}>{heading}</h1>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t("serviceDate")}</span>
          <input
            type="date"
            value={date}
            onChange={(e) => void navigate({ search: { date: e.target.value || undefined }, replace: true, resetScroll: false })}
            className="rounded-md border border-slate-300 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900"
          />
          <BsDateHint date={date} />
        </label>
      </div>

      {can.manage(role) && <FleetAlerts />}

      {/* Not a live region: the counts refresh every 15 seconds. */}
      <div>
        {trips.isPending && <Loading />}
        {trips.isError && !trips.data && !unauthenticated && <QueryError onRetry={() => void trips.refetch()} busy={trips.isFetching} />}
        {trips.data?.trips.length === 0 && <Empty>{t("noOperatorTrips")}</Empty>}
        {trips.data && trips.data.trips.length > 0 && (
          <ul className="space-y-3">
            {trips.data.trips.map((trip) => (
              <TripCard key={trip.id} trip={trip} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function TripCard({ trip }: { trip: Trip }) {
  const { lang } = useI18n();
  const departs = Number(trip.departsAtMs);

  return (
    <li>
      <Link
        to="/operator/trips/$tripId"
        params={{ tripId: trip.id }}
        className="block space-y-3 rounded-xl border border-slate-200 p-4 hover:border-ink/40 hover:bg-slate-50/60 dark:border-slate-800 dark:hover:border-ink-dark/40 dark:hover:bg-slate-900/60"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className={ui.h1}>
              <time dateTime={new Date(departs).toISOString()}>{formatNepalTime(departs, lang)}</time>
            </p>
            <p className="font-medium">{trip.routeName?.[lang] || trip.routeCode}</p>
            <TripVehicle trip={trip} />
          </div>
          <TripStatusBadge status={trip.status} />
        </div>
        <PermitWarning trip={trip} />
        <SeatCounts trip={trip} />
      </Link>
    </li>
  );
}
